import { type ReactNode, type HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

/**
 * Canonical typography tokens for the "label + description" block that
 * sits inside ControlRow (and any other component that wants the same
 * compact caption-style pair). Exported so sibling graphics like
 * SurchargeScopeComparison and SurchargeMethodSelector — which can't use
 * ControlRow directly because of their custom responsive-wrap layouts —
 * can still share the exact same type sizes instead of hand-copying the
 * Tailwind string.
 *
 * These intentionally use the mobile 11px / desktop 12px scale after the
 * Phase B audit pass; see
 * docs/help-sheet-homogenization-principles.md §3.
 */
export const CONTROL_ROW_LABEL_CLASS =
  'font-label text-[12px] sm:text-[13px] font-semibold leading-none uppercase tracking-[0.5px] text-foreground/90';

export const CONTROL_ROW_BODY_CLASS =
  'font-body text-[12px] sm:text-[13px] leading-[1.4] text-foreground/85 mt-1';

interface ControlRowProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  description: ReactNode;
  children: ReactNode;
  layout?: 'row' | 'responsive' | 'stack';
}

export function ControlRow({
  label,
  description,
  children,
  layout = 'row',
  className,
  ...rest
}: ControlRowProps) {
  if (layout === 'row') {
    return (
      <div
        className={cn(
          'px-3 py-2',
          'grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center',
          'sm:flex sm:items-center sm:justify-between sm:gap-4',
          className,
        )}
        {...rest}
      >
        <div className="min-w-0">
          <span className={CONTROL_ROW_LABEL_CLASS}>{label}</span>
          <p className={cn(CONTROL_ROW_BODY_CLASS, 'hidden sm:block')}>{description}</p>
        </div>
        {children}
        <p className={cn(CONTROL_ROW_BODY_CLASS, 'col-span-2 sm:hidden')}>{description}</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'px-3 py-2',
        layout === 'responsive' && 'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
        layout === 'stack' && 'flex flex-col gap-3',
        className,
      )}
      {...rest}
    >
      <div className="min-w-0">
        <span className={CONTROL_ROW_LABEL_CLASS}>{label}</span>
        <p className={CONTROL_ROW_BODY_CLASS}>{description}</p>
      </div>
      {children}
    </div>
  );
}
